import { useEffect } from "react";
import i18n from "i18next";
import {
  Outlet,
  useNavigate,
  useParams,
  useMatch,
  useLocation,
} from "react-router-dom";
import styled from "styled-components";
import { DEFAULT_LOCALE, LOCALES, getLocalizedPath } from "../services/locale";
import * as flags from "../flags";
import { Menu } from "./Menu";
import { Footer } from "./Footer";
import { COLORS, MOBILE_BREAKPOINT, flexColumn, flexRow } from "./style";

export const PAGE_BASE_VERTICAL_PADDING = 30 as const;
export const PAGE_BASE_HORIZONTAL_PADDING = {
  DESKTOP: 60,
  MOBILE: 15,
} as const;

const ContentBase = styled.div`
  flex-grow: 1;
  padding: ${`${PAGE_BASE_VERTICAL_PADDING}px ${PAGE_BASE_HORIZONTAL_PADDING.DESKTOP}px`};
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: ${`${PAGE_BASE_VERTICAL_PADDING}px ${PAGE_BASE_HORIZONTAL_PADDING.MOBILE}px`};
  }
`;

const Base = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;

  #footer {
    background-color: ${COLORS.BLACK};

    ${flexRow}
    justify-content: space-between;
    padding: 30px 60px;

    .partners {
      flex: 3;
      ${flexRow}

      .partner-logos {
        ${flexRow}
      }
    }

    .nav-link-grid {
      flex: 1;
      display: grid;
      grid-template-columns: 1fr 1fr;
      column-gap: 30px;

      a {
        text-decoration-color: ${COLORS.WHITE};
      }
    }

    .social-links {
      flex: 1;
      span {
        margin-bottom: 0.5rem;
        display: block;
      }
    }

    @media (max-width: ${MOBILE_BREAKPOINT}) {
      ${flexColumn}
      .social-links, .nav-link-grid {
        margin: 20px 0;
      }
    }
  }
`;

const NO_SCROLL_RESTORE = ["/faq", "/schedule"];

export const PageBase = () => {
  const { pathname } = useLocation();
  const { locale: currentLocale } = useParams() as {
    locale: keyof typeof LOCALES;
  };
  const navigate = useNavigate();
  const match = useMatch(":locale/*");
  const currentPath = (match?.params as { locale: string; "*": string })["*"];

  useEffect(() => {
    if (!NO_SCROLL_RESTORE.some((path) => pathname.includes(path))) {
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  useEffect(() => {
    if (flags.LOCALIZATION && LOCALES[currentLocale] !== undefined) {
      // set language if supported
      i18n.changeLanguage(currentLocale);
    } else if (currentLocale !== DEFAULT_LOCALE) {
      // re-route to page with EN locale
      navigate(getLocalizedPath(DEFAULT_LOCALE, currentPath));
    }
  }, [navigate, currentLocale, currentPath]);

  return (
    <Base>
      <Menu currentLocale={currentLocale} currentPage={currentPath} />
      <ContentBase>
        <Outlet />
      </ContentBase>
      <Footer currentLocale={currentLocale} />
    </Base>
  );
};
